// Global Instructions Rule Applied!
//
// Pipeline Meetings — UI HTTP service (design §8.E). Talks to the Python
// backend only (CLAUDE.md — never Supabase directly). Every response is
// strict-validated with its Zod schema before it reaches UI state.

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  PipelineMeetingList,
  PipelineMeetingDetail,
  PipelineMeetingThemes,
  PipelineMeetingFollowUpList,
  MinutesVersionList,
  validatePipelineMeetingList,
  validatePipelineMeetingDetail,
  validatePipelineMeetingThemes,
  validatePipelineMeetingFollowUpList,
  validateMinutesVersionList
} from '../../../state_manager/data_models/pipeline_meetings_model.ts'
import { readJsonBody } from '../../../core/utils/utilities.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

const jsonHeaders = (accessToken: string | null): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

const readBody = async (response: Response, failureLabel: string): Promise<unknown> => {
  const body = await readJsonBody<{ data?: unknown; error?: string; message?: string }>(response)
  if (!response.ok) {
    throw new Error(
      body?.message || body?.error || `${failureLabel}: ${response.status} ${response.statusText}`
    )
  }
  return body?.data
}

export const fetchPipelineMeetings = async (
  params: { series_id?: string; page?: number; page_size?: number },
  accessToken: string | null = null
): Promise<PipelineMeetingList> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.PIPELINE_MEETINGS)
  const response = await fetch(url, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: params })
  })
  return validatePipelineMeetingList(await readBody(response, 'Meeting list failed'))
}

export const fetchPipelineMeetingDetail = async (
  meetingId: string,
  accessToken: string | null = null
): Promise<PipelineMeetingDetail> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.PIPELINE_MEETINGS)
  const response = await fetch(`${base}/${meetingId}`, {
    method: 'GET',
    headers: jsonHeaders(accessToken)
  })
  return validatePipelineMeetingDetail(await readBody(response, 'Meeting detail failed'))
}

export const fetchPipelineMeetingThemes = async (
  accessToken: string | null = null,
  seriesId?: string
): Promise<PipelineMeetingThemes> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.PIPELINE_MEETING_THEMES)
  const url = seriesId ? `${base}?series_id=${encodeURIComponent(seriesId)}` : base
  const response = await fetch(url, { method: 'GET', headers: jsonHeaders(accessToken) })
  return validatePipelineMeetingThemes(await readBody(response, 'Investment themes failed'))
}

// ── One meeting's follow-ups ──────────────────────────────────────────────

export const fetchPipelineMeetingFollowUps = async (
  meetingId: string,
  accessToken: string | null = null
): Promise<PipelineMeetingFollowUpList> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.PIPELINE_MEETING_FOLLOW_UPS)
  const response = await fetch(url, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: { meeting_id: meetingId } })
  })
  return validatePipelineMeetingFollowUpList(await readBody(response, 'Follow-ups failed'))
}

export const setPipelineMeetingFollowUpStatus = async (
  followUpId: string,
  status: 'open' | 'done',
  accessToken: string | null = null
): Promise<unknown> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.PIPELINE_MEETING_FOLLOW_UPS)
  const response = await fetch(`${base}/${followUpId}/status`, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: { status } })
  })
  return readBody(response, 'Follow-up update failed')
}

/** Raise a task from a follow-up. The reviewed task record travels with the
 *  deal the user picked (pre-filled with the follow-up's deal); the backend
 *  links the task to that deal and its company — the createTaskFromSignal /
 *  createTaskFromState pattern. */
export const createTaskFromFollowUp = async (
  followUpId: string,
  taskRecord: unknown,
  dealId: string | null,
  accessToken: string | null = null
): Promise<unknown> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.PIPELINE_MEETING_FOLLOW_UPS)
  const response = await fetch(`${base}/${followUpId}/create-task`, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: { task: taskRecord, deal_id: dealId } })
  })
  return readBody(response, 'Task creation failed')
}

// ── People's edits of a follow-up ─────────────────────────────────────────

export interface FollowUpEditFields {
  description: string
  owner_contact_id: string | null
  due_date: string | null
  deal_id: string | null
}

export const editPipelineMeetingFollowUp = async (
  followUpId: string,
  fields: FollowUpEditFields,
  accessToken: string | null = null
): Promise<unknown> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.PIPELINE_MEETING_FOLLOW_UPS)
  const response = await fetch(`${base}/${followUpId}/edit`, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: fields })
  })
  return readBody(response, 'Follow-up edit failed')
}

export const addPipelineMeetingFollowUp = async (
  meetingId: string,
  fields: FollowUpEditFields,
  accessToken: string | null = null
): Promise<unknown> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.PIPELINE_MEETING_FOLLOW_UP_ADD)
  const response = await fetch(url, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: { meeting_id: meetingId, ...fields } })
  })
  return readBody(response, 'Follow-up add failed')
}

export const removePipelineMeetingFollowUp = async (
  followUpId: string,
  accessToken: string | null = null
): Promise<unknown> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.PIPELINE_MEETING_FOLLOW_UPS)
  const response = await fetch(`${base}/${followUpId}/remove`, {
    method: 'POST',
    headers: jsonHeaders(accessToken)
  })
  return readBody(response, 'Follow-up remove failed')
}

// ── People's edits of the minutes (versions on the doc's supersede chain) ──

export const fetchPipelineMeetingMinutesHistory = async (
  meetingId: string,
  accessToken: string | null = null
): Promise<MinutesVersionList> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.PIPELINE_MEETINGS)
  const response = await fetch(`${base}/${meetingId}/minutes/history`, {
    method: 'GET',
    headers: jsonHeaders(accessToken)
  })
  return validateMinutesVersionList(await readBody(response, 'Minutes history failed'))
}

export const editPipelineMeetingMinutes = async (
  meetingId: string,
  content: string,
  accessToken: string | null = null
): Promise<unknown> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.PIPELINE_MEETINGS)
  const response = await fetch(`${base}/${meetingId}/minutes`, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: { content } })
  })
  return readBody(response, 'Minutes save failed')
}

export const restorePipelineMeetingMinutes = async (
  meetingId: string,
  docId: string,
  accessToken: string | null = null
): Promise<unknown> => {
  const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.INTERNAL_AGENTS.PIPELINE_MEETINGS)
  const response = await fetch(`${base}/${meetingId}/minutes/restore`, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ data: { doc_id: docId } })
  })
  return readBody(response, 'Minutes restore failed')
}
